import React, { useState, useEffect } from 'react';
import { useAsset } from '../../context/AssetContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Skeleton } from '../ui/skeleton';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import {
  ArrowLeft, User as UserIcon, Mail, Hash, Briefcase,
  Monitor, History, ExternalLink, Calendar, Package, FolderKanban
} from 'lucide-react';

const formatDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString(); } catch { return d; }
};
const formatDateTime = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleString(); } catch { return d; }
};

const InfoRow = ({ icon: Icon, label, value, mono = false }) => (
  <div className="flex items-start gap-3 py-2.5">
    {Icon && <Icon className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />}
    <div className="flex-1 min-w-0">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-sm ${mono ? 'font-mono' : 'font-medium'} break-words`}>{value || '-'}</p>
    </div>
  </div>
);

const getStatusBadgeVariant = (status) => {
  switch (status) {
    case 'Employed': return 'default';
    case 'Resigned': return 'secondary';
    case 'On Leave': return 'outline';
    case 'Departed': return 'outline';
    default: return 'outline';
  }
};

const EmployeeDetail = ({ employeeId, onBack, onViewAsset }) => {
  const {
    getEmployee, getEmployeeAllocatedAssets,
    getEmployeeAssetAllocationHistory, getEmployeeUpdateHistory,
  } = useAsset();

  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allocatedAssets, setAllocatedAssets] = useState([]);
  const [allocHistory, setAllocHistory] = useState([]);
  const [updateHistory, setUpdateHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (!employeeId) return;
    loadEmployee();
    loadAllocHistory();
    loadUpdateHistory();
  }, [employeeId]);

  const loadEmployee = async () => {
    setLoading(true);
    const data = await getEmployee(employeeId);
    setEmployee(data);
    setAllocatedAssets(data?.allocated_assets || []);
    setLoading(false);
  };

  const loadAllocHistory = async () => {
    setHistoryLoading(true);
    const data = await getEmployeeAssetAllocationHistory(employeeId);
    setAllocHistory(Array.isArray(data) ? data : []);
    setHistoryLoading(false);
  };

  const loadUpdateHistory = async () => {
    setUpdateLoading(true);
    const data = await getEmployeeUpdateHistory(employeeId);
    setUpdateHistory(Array.isArray(data) ? data : []);
    setUpdateLoading(false);
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="employee-detail-loading">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="text-center py-12" data-testid="employee-detail-notfound">
        <p className="text-muted-foreground mb-4">Employee not found.</p>
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="employee-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" onClick={onBack} data-testid="employee-detail-back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h2 className="text-2xl font-bold tracking-tight" data-testid="employee-detail-name">
                {employee.employee_name}
              </h2>
              <Badge variant="outline" className="font-mono">{employee.employee_id}</Badge>
              <Badge variant={getStatusBadgeVariant(employee.employee_status)}>
                {employee.employee_status}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground flex items-center gap-1.5">
              <Mail className="h-3.5 w-3.5" />
              {employee.email}
            </p>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview" data-testid="employee-detail-tab-overview">
            <UserIcon className="h-4 w-4 mr-2" /> Overview
          </TabsTrigger>
          <TabsTrigger value="allocation" data-testid="employee-detail-tab-allocation">
            <Monitor className="h-4 w-4 mr-2" /> Allocation History
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="employee-detail-tab-history">
            <History className="h-4 w-4 mr-2" /> Employee Update History
          </TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Profile</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <InfoRow icon={Hash} label="Employee ID" value={employee.employee_id} mono />
                <Separator />
                <InfoRow icon={UserIcon} label="Full Name" value={employee.employee_name} />
                <Separator />
                <InfoRow icon={Mail} label="Email" value={employee.email} />
                <Separator />
                <InfoRow icon={Briefcase} label="Status" value={employee.employee_status} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Current Project</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {employee.current_project ? (
                  <>
                    <InfoRow icon={FolderKanban} label="Project Name" value={employee.current_project.project_name} />
                    <Separator />
                    <InfoRow icon={Hash} label="Project ID" value={employee.current_project.project_id} mono />
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground italic py-3">Not currently allocated to any project.</p>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center justify-between">
                <span>Currently Allocated Assets</span>
                <Badge variant="secondary">{allocatedAssets.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {allocatedAssets.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground" data-testid="employee-detail-no-current-assets">
                  No assets currently allocated to this employee.
                </div>
              ) : (
                <div className="space-y-2" data-testid="employee-detail-current-assets-list">
                  {allocatedAssets.map((a) => (
                    <div
                      key={a.asset_id}
                      className="border rounded-lg p-3 flex items-center justify-between flex-wrap gap-3 hover:bg-accent cursor-pointer transition-colors"
                      onClick={() => onViewAsset && onViewAsset(a.asset_id)}
                      data-testid={`employee-detail-current-asset-${a.asset_id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <Package className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{a.asset_name}</p>
                          <p className="text-xs text-muted-foreground font-mono">{a.asset_id} · {a.asset_type}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Badge variant="outline">{a.status}</Badge>
                        <ExternalLink className="h-3.5 w-3.5" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Allocation History */}
        <TabsContent value="allocation" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Assets Allocated During Tenure</CardTitle>
            </CardHeader>
            <CardContent>
              {historyLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map(i => <Skeleton key={i} className="h-14 w-full" />)}
                </div>
              ) : allocHistory.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground" data-testid="employee-detail-alloc-history-empty">
                  This employee has never been allocated any asset.
                </div>
              ) : (
                <ScrollArea className="max-h-[500px] pr-4">
                  <div className="space-y-2" data-testid="employee-detail-alloc-history-list">
                    {allocHistory.map((row, idx) => (
                      <div
                        key={`${row.asset_id}-${idx}`}
                        className="border rounded-lg p-3 flex items-center justify-between flex-wrap gap-3 hover:bg-accent cursor-pointer transition-colors"
                        onClick={() => row.asset_id && onViewAsset && onViewAsset(row.asset_id)}
                        data-testid={`employee-detail-alloc-history-row-${idx}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <Package className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{row.asset_name || row.asset_id}</p>
                            <p className="text-xs text-muted-foreground font-mono">
                              {row.asset_id}{row.asset_type ? ` · ${row.asset_type}` : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 flex-wrap text-xs text-muted-foreground">
                          <span className="inline-flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            From: {row.allocated_at ? formatDateTime(row.allocated_at) : '—'}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            To: {row.unallocated_at ? formatDateTime(row.unallocated_at) : '—'}
                          </span>
                          <Badge variant={row.status === 'Active' ? 'default' : 'outline'}>
                            {row.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Update History */}
        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Employee Record Changes</CardTitle>
            </CardHeader>
            <CardContent>
              {updateLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map(i => <Skeleton key={i} className="h-14 w-full" />)}
                </div>
              ) : updateHistory.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground" data-testid="employee-detail-update-history-empty">
                  No update history for this employee.
                </div>
              ) : (
                <ScrollArea className="max-h-[500px] pr-4">
                  <div className="space-y-2" data-testid="employee-detail-update-history-list">
                    {updateHistory.map((log, idx) => (
                      <div key={log.id || idx} className="border rounded-lg p-3" data-testid={`employee-detail-update-log-${idx}`}>
                        <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
                          <Badge
                            variant={
                              log.action?.includes('CREATED') || log.action?.includes('ADDED') ? 'default' :
                              log.action?.includes('DELETED') ? 'destructive' :
                              log.action?.includes('UPDATED') ? 'secondary' : 'outline'
                            }
                          >
                            {log.action}
                          </Badge>
                          <span className="text-xs text-muted-foreground">{formatDateTime(log.timestamp)}</span>
                        </div>
                        <p className="text-sm">{log.details}</p>
                        <p className="text-xs text-muted-foreground mt-1">By: {log.username}</p>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EmployeeDetail;
